import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { ACCEPTED_EXTENSIONS } from '@/pages/RoIntake/constants'

import { isRemovableFile } from './queue'

const wrapFile = (raw) => ({ id: crypto.randomUUID(), raw })

// Long enough that sweeping the mouse down a column does not flash a card on every row.
const HOVER_DELAY_MS = 300

/**
 * The file names, on hover, so a glance answers "is the invoice on this one?" without a
 * click (Mark, 2026-10-08: "like Outlook"). Fixed and in a portal, so the table's own
 * scroll box does not clip it.
 */
const FileNamesCard = ({ anchor, names }) => {
  const [card, setCard] = useState(null)
  const { styles, attributes } = usePopper(anchor, card, {
    placement: 'bottom-end',
    strategy: 'fixed',
    modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
  })

  return createPortal(
    <div
      className="pointer-events-none z-40 max-w-xs rounded-lg bg-white px-3 py-2 shadow-lg ring-1 ring-black/5"
      ref={setCard}
      role="tooltip"
      style={styles.popper}
      {...attributes.popper}
    >
      <ul className="space-y-0.5">
        {names.map((name, index) => (
          // Two files can share a name.
          // eslint-disable-next-line react/no-array-index-key
          <li className="text-charcoal-800 flex items-center gap-1.5 text-xs" key={index}>
            <PaperClipIcon aria-hidden className="text-charcoal-400 h-3 w-3 shrink-0" />
            <span className="truncate">{name}</span>
          </li>
        ))}
      </ul>
    </div>,
    document.body,
  )
}

FileNamesCard.propTypes = {
  anchor: PropTypes.object,
  names: PropTypes.arrayOf(PropTypes.string).isRequired,
}

/**
 * A row's attachments, managed without leaving the table (WARP-951, WARP-966): a paper
 * clip with the count when the RO has files, their names on hover, and a click to open,
 * add, or remove them.
 *
 * Changes are staged, not sent: new files and removals wait for the row's Save, the same
 * as its status, so one Save commits everything done to the row.
 *
 * Who may do what: anyone can open the files. `canAdd` adds files (dealers too, WARP-966).
 * `canRemove` removes saved ones; without it, only files added here and not yet saved can
 * be taken back. Line-item files list with the RO's but are removed on the RO page.
 */
const AttachmentsPopover = ({
  canAdd,
  canRemove,
  disabled,
  files,
  filesToDelete,
  newFiles,
  onChange,
  roNumber,
  size,
}) => {
  const inputRef = useRef(null)
  const hoverTimer = useRef(null)
  const [anchor, setAnchor] = useState(null)
  const [hovering, setHovering] = useState(false)

  useEffect(() => () => clearTimeout(hoverTimer.current), [])

  const kept = files.filter((file) => !filesToDelete.includes(file.id))
  const count = kept.length + newFiles.length
  const staged = newFiles.length > 0 || filesToDelete.length > 0
  const names = [
    ...kept.map((file) => file.displayName || 'File'),
    ...newFiles.map((entry) => entry.raw.name),
  ]

  // Nothing to show and nothing allowed: no control, only a quiet blank.
  if (count === 0 && !canAdd) {
    return (
      <span aria-label="No attachments" className="text-charcoal-300 text-xs">
        —
      </span>
    )
  }

  const startHover = () => {
    clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => setHovering(true), HOVER_DELAY_MS)
  }
  const endHover = () => {
    clearTimeout(hoverTimer.current)
    setHovering(false)
  }

  let label = `Attachments for RO ${roNumber}: ${count} file${count === 1 ? '' : 's'}`
  if (count === 0) label = `Add attachments to RO ${roNumber}`

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <PopoverButton
            aria-label={label}
            className={mergeClassNames(
              'flex items-center gap-1 rounded-md border font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-800',
              size === 'sm' ? 'h-7 px-1.5 text-[11px]' : 'h-8 px-2 text-xs',
              count > 0
                ? 'border-charcoal-200 text-charcoal-700 hover:border-blue-800 hover:text-blue-800'
                : // No files yet: a faint clip, there to add one, not to draw the eye.
                  'text-charcoal-300 hover:border-charcoal-200 border-transparent hover:text-blue-800',
              staged && 'border-blue-800 bg-blue-50 text-blue-800',
            )}
            onBlur={endHover}
            onClick={endHover}
            onMouseEnter={count > 0 ? startHover : undefined}
            onMouseLeave={endHover}
            ref={setAnchor}
          >
            <PaperClipIcon aria-hidden className="h-4 w-4" />
            {count > 0 && <span className="tabular-nums">{count}</span>}
          </PopoverButton>

          {hovering && !open && count > 0 && <FileNamesCard anchor={anchor} names={names} />}

          <PopoverPanel
            anchor="bottom end"
            className="z-30 mt-1 w-72 rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            <p className="text-charcoal-700 text-[10px] font-bold uppercase tracking-widest">
              Attachments · RO {roNumber}
            </p>

            {count === 0 && <p className="text-charcoal-400 mt-2 text-xs">No files yet.</p>}

            <ul className="mt-2 space-y-1">
              {kept.map((file) => (
                <li className="flex items-center justify-between gap-2" key={file.id}>
                  <a
                    className="truncate text-sm text-blue-800 underline"
                    href={file.file}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {file.displayName || 'File'}
                  </a>
                  {!isRemovableFile(file) && (
                    <span
                      className="text-charcoal-400 shrink-0 text-[10px]"
                      title="Added to a line item. Remove it from the RO page."
                    >
                      Line item
                    </span>
                  )}
                  {isRemovableFile(file) && canRemove && (
                    <button
                      aria-label={`Remove ${file.displayName || 'file'}`}
                      className="text-charcoal-400 hover:text-error shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={disabled}
                      onClick={() => onChange({ filesToDelete: [...filesToDelete, file.id] })}
                      type="button"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
              {newFiles.map((entry) => (
                <li className="flex items-center justify-between gap-2" key={entry.id}>
                  <span className="text-charcoal-900 truncate text-sm">
                    {entry.raw.name}
                    <span className="text-charcoal-400 ml-1 text-xs italic">(not saved)</span>
                  </span>
                  <button
                    aria-label={`Remove ${entry.raw.name}`}
                    className="text-charcoal-400 hover:text-error shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={disabled}
                    onClick={() =>
                      onChange({ newFiles: newFiles.filter((item) => item.id !== entry.id) })
                    }
                    type="button"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>

            {canAdd && (
              <>
                <input
                  accept={ACCEPTED_EXTENSIONS.join(',')}
                  className="hidden"
                  multiple
                  onChange={(event) => {
                    if (event.target.files.length > 0) {
                      onChange({
                        newFiles: [...newFiles, ...Array.from(event.target.files).map(wrapFile)],
                      })
                    }
                    // Cleared so picking the same file again still fires a change.
                    if (inputRef.current) inputRef.current.value = ''
                  }}
                  ref={inputRef}
                  type="file"
                />
                <button
                  className="border-charcoal-200 text-charcoal-700 mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs font-semibold hover:border-blue-800 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={disabled}
                  onClick={() => inputRef.current?.click()}
                  type="button"
                >
                  <PaperClipIcon className="h-4 w-4" />
                  Add files
                </button>
              </>
            )}

            {staged && (
              <p className="text-charcoal-500 mt-2 text-[11px]">
                Changes here save with the row. Press its Save.
              </p>
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  )
}

AttachmentsPopover.propTypes = {
  canAdd: PropTypes.bool,
  canRemove: PropTypes.bool,
  // While the row saves.
  disabled: PropTypes.bool,
  files: PropTypes.arrayOf(PropTypes.object),
  filesToDelete: PropTypes.arrayOf(PropTypes.string).isRequired,
  newFiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  roNumber: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['md', 'sm']),
}

AttachmentsPopover.defaultProps = {
  canAdd: false,
  canRemove: false,
  disabled: false,
  // The RO list leaves `files` out when there are none.
  files: [],
  size: 'md',
}

export default AttachmentsPopover
